export const RECOMMENDATION_LIMIT = 3
export const BLOG_FEED_PAGE_SIZE = 50

export function createListenerScope() {
  const controller = new AbortController()

  return {
    signal: controller.signal,
    listen(target, type, listener, options = {}) {
      target?.addEventListener?.(type, listener, { ...options, signal: controller.signal })
    },
    abort() {
      controller.abort()
    },
  }
}

export function createRequestGate() {
  let generation = 0
  let controller

  return {
    begin() {
      controller?.abort()
      controller = new AbortController()
      const currentGeneration = ++generation
      const currentController = controller

      return {
        signal: currentController.signal,
        isCurrent: () => currentGeneration === generation && !currentController.signal.aborted,
      }
    },
    abort() {
      controller?.abort()
      generation += 1
    },
  }
}

function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
}

export function uniqueHeadingId(text, index, used, isTaken = () => false) {
  const seed =
    normalize(text)
      .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
      .replace(/^-+|-+$/gu, '') || `heading-${index + 1}`
  let candidate = seed
  let suffix = 2

  while (used.has(candidate) || isTaken(candidate)) candidate = `${seed}-${suffix++}`
  used.add(candidate)
  return candidate
}

export function groupArticleHeadings(headings) {
  const groups = []

  for (const heading of headings || []) {
    if (String(heading?.tagName).toLowerCase() === 'h2') {
      groups.push({ heading, children: [] })
      continue
    }

    if (!groups.length) groups.push({ heading: null, children: [] })
    groups.at(-1).children.push(heading)
  }

  return groups
}

export function activeArticleHeading(headings, offset = 140) {
  const available = [...(headings || [])]
  if (!available.length) return null
  let active = available[0]

  for (const heading of available) {
    if (heading.getBoundingClientRect?.().top <= offset) active = heading
  }

  return active
}

export function scrollTopForHeading(heading, offset = 140, scrollY = globalThis.scrollY || 0) {
  const top = heading?.getBoundingClientRect?.().top
  if (!Number.isFinite(top)) return Math.max(0, scrollY)
  return Math.max(0, scrollY + top - offset)
}

export function setTocAvailability(toc, tocRegion, toggle, available) {
  if (available) {
    tocRegion?.removeAttribute?.('hidden')
    toggle?.removeAttribute?.('hidden')
    return
  }

  toc?.replaceChildren?.()
  tocRegion?.setAttribute?.('hidden', '')
  toggle?.setAttribute?.('hidden', '')
}

export function rankRecommendations(
  items,
  { currentUrl, currentTags = [], currentBlogTitle, limit = RECOMMENDATION_LIMIT },
) {
  const unique = new Map()
  const currentTagSet = new Set(currentTags.map(normalize).filter(Boolean))

  for (const item of items || []) {
    if (!item?.url || item.url === currentUrl || unique.has(item.url)) continue
    unique.set(item.url, item)
  }

  const sorted = [...unique.values()].sort((left, right) => {
    const dateDifference = Number(right.updatedAt || 0) - Number(left.updatedAt || 0)
    return dateDifference || String(left.url).localeCompare(String(right.url))
  })
  const tagMatches = sorted.filter((item) =>
    (item.tags || []).some((tag) => currentTagSet.has(normalize(tag))),
  )
  const selected = new Set(tagMatches.map(({ url }) => url))
  const sameBlog = sorted.filter(
    (item) => !selected.has(item.url) && normalize(item.blogTitle) === normalize(currentBlogTitle),
  )
  sameBlog.forEach(({ url }) => selected.add(url))
  const latest = sorted.filter((item) => !selected.has(item.url))

  return [...tagMatches, ...sameBlog, ...latest].slice(0, limit)
}

export function cartPathForVariant(variantId, cartUrl = '/cart') {
  if (!variantId) return ''
  return `${String(cartUrl || '/cart').replace(/\/$/u, '')}/${variantId}:1`
}

export function applyVariantSelection(root, option) {
  if (!root || !option) return
  const available = !option.disabled && option.dataset?.available !== 'false'
  const input = root.querySelector('[name="id"]')
  const price = root.querySelector('[data-article-product-price]')
  const comparePrice = root.querySelector('[data-article-product-compare]')
  const image = root.querySelector('[data-article-product-image]')
  const buyNow = root.querySelector('[data-article-buy-now]')
  const submit = root.querySelector('[type="submit"]')

  if (input) input.value = option.value
  if (price && option.dataset?.price !== undefined) price.textContent = option.dataset.price
  if (comparePrice && option.dataset?.comparePrice !== undefined) {
    comparePrice.textContent = option.dataset.comparePrice
    comparePrice.hidden = !option.dataset.comparePrice
  }
  if (image && option.dataset?.image) {
    image.src = option.dataset.image
    image.srcset = ''
  }
  if (submit) submit.disabled = !available
  if (buyNow) {
    if (available) {
      buyNow.href = cartPathForVariant(option.value, buyNow.dataset?.cartUrl || '/cart')
      buyNow.removeAttribute?.('aria-disabled')
    } else {
      buyNow.removeAttribute?.('href')
      buyNow.setAttribute?.('aria-disabled', 'true')
    }
  }
}

function blogPageUrl(handle, page, locationHref) {
  const current = new URL(locationHref || 'https://shopify.invalid')
  const url = new URL(`/blogs/${encodeURIComponent(handle)}`, current.origin)
  url.searchParams.set('view', 'articles-json')
  url.searchParams.set('page', String(page))
  if (current.searchParams.has('preview_theme_id')) {
    url.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'))
  }
  return `${url.pathname}${url.search}`
}

async function fetchBlogPage(handle, page, { fetchImpl, signal, locationHref }) {
  const response = await fetchImpl(blogPageUrl(handle, page, locationHref), { signal })
  if (!response.ok) throw new Error(`Article recommendations unavailable: ${handle}`)
  return response.json()
}

export async function loadRecommendationArticles(
  blogs,
  { fetchImpl = globalThis.fetch, signal, locationHref = globalThis.location?.href } = {},
) {
  const requests = []

  for (const blog of blogs || []) {
    if (!blog?.handle) continue
    const pages = Math.max(1, Math.ceil(Number(blog.count || 0) / BLOG_FEED_PAGE_SIZE))
    for (let page = 1; page <= pages; page += 1) {
      requests.push(fetchBlogPage(blog.handle, page, { fetchImpl, signal, locationHref }))
    }
  }

  const settled = await Promise.allSettled(requests)
  const articles = []
  let failed = requests.length === 0

  for (const result of settled) {
    if (result.status === 'fulfilled') articles.push(...(result.value.articles || []))
    else if (result.reason?.name === 'AbortError') throw result.reason
    else failed = true
  }

  return { articles, failed }
}

function parseJSON(value, fallback) {
  try {
    return JSON.parse(value || '')
  } catch {
    return fallback
  }
}

function element(documentRef, name, className, text) {
  const node = documentRef.createElement(name)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

const HTMLElementBase = globalThis.HTMLElement ?? class {}

export class ArticleDefaultMain extends HTMLElementBase {
  connectedCallback() {
    this.disconnectedCallback()
    this._scope = createListenerScope()
    this._initializeInsertedBlocks()
    this._initializeProducts()
    this._initializeToc()
    this._initializeSidebar()
  }

  disconnectedCallback() {
    this._scope?.abort()
    this._scope = undefined
  }

  get listenerSignal() {
    return this._scope?.signal
  }

  _initializeInsertedBlocks() {
    const content = this.querySelector?.('[data-article-default-content]')
    if (!content?.querySelectorAll) return
    const headings = [...content.querySelectorAll('h2')]
    const eventTemplate = this.querySelector('[data-article-event-template]')
    const productTemplate = this.querySelector('[data-article-product-template]')

    this._insertTemplate(eventTemplate, headings[1], 'event', content)
    this._insertTemplate(productTemplate, headings[2] || headings[1], 'product', content)
  }

  _insertTemplate(template, target, type, fallback) {
    if (!template?.content || (!target?.parentNode && !fallback?.append)) return
    if (template.content.childElementCount === 0) return
    if (!template.content.querySelector?.(`[data-article-${type}]`)) return
    if (this.querySelector?.(`[data-article-inserted="${type}"]`)) return
    const wrapper = this.ownerDocument?.createElement?.('div')
    if (!wrapper) return
    wrapper.dataset.articleInserted = type
    wrapper.className = `article-default__insert article-default__insert--${type}`
    wrapper.append(template.content.cloneNode(true))
    if (target?.parentNode) target.parentNode.insertBefore(wrapper, target)
    else fallback.append(wrapper)
  }

  _initializeProducts() {
    for (const product of this.querySelectorAll?.('[data-article-product]') || []) {
      const select = product.querySelector('[data-article-product-select]')
      if (!select) continue
      const update = () => applyVariantSelection(product, select.selectedOptions?.[0])
      this._scope.listen(select, 'change', update)
      update()
    }
  }

  _initializeToc() {
    const content = this.querySelector?.('[data-article-default-content]')
    const toc = this.querySelector?.('[data-article-default-toc]')
    if (!content?.querySelectorAll || !toc?.replaceChildren || !this.ownerDocument) return
    const headings = [...content.querySelectorAll('h2, h3')].filter(
      (heading) => !heading.closest?.('[data-article-inserted]') && heading.textContent?.trim(),
    )
    const tocRegion = this.querySelector?.('[data-article-default-toc-region]')
    const toggle = this.querySelector?.('[data-article-sidebar-toggle]')
    setTocAvailability(toc, tocRegion, toggle, headings.length > 0)
    if (!headings.length) {
      return
    }

    const used = new Set()
    const list = element(this.ownerDocument, 'ol', 'article-default__toc-list')
    const linksByHeading = new Map()

    for (const [index, heading] of headings.entries()) {
      const existingId = heading.id?.trim()
      const isTaken = (candidate) => {
        const owner = this.ownerDocument.getElementById?.(candidate)
        return Boolean(owner && owner !== heading)
      }

      if (existingId && !used.has(existingId) && !isTaken(existingId)) {
        used.add(existingId)
        continue
      }

      if (existingId) used.add(existingId)
      heading.id = uniqueHeadingId(existingId || heading.textContent, index, used, isTaken)
    }

    const createLink = (heading, level) => {
      const link = element(
        this.ownerDocument,
        'a',
        `article-default__toc-link article-default__toc-link--${level}`,
        heading.textContent.trim(),
      )
      link.href = `#${heading.id}`
      this._scope.listen(link, 'click', (event) => {
        event.preventDefault()
        const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        globalThis.scrollTo?.({
          top: scrollTopForHeading(heading),
          behavior: reduceMotion ? 'auto' : 'smooth',
        })
        globalThis.history?.replaceState?.(null, '', `#${heading.id}`)
        setActive(heading)
      })
      linksByHeading.set(heading, link)
      return link
    }

    for (const group of groupArticleHeadings(headings)) {
      const groupItem = element(this.ownerDocument, 'li', 'article-default__toc-group')

      if (group.heading) {
        const h2Item = element(
          this.ownerDocument,
          'div',
          'article-default__toc-item article-default__toc-item--h2',
        )
        h2Item.append(createLink(group.heading, 'h2'))
        groupItem.append(h2Item)
      }

      if (group.children.length) {
        const sublist = element(this.ownerDocument, 'ol', 'article-default__toc-sublist')
        for (const heading of group.children) {
          const item = element(
            this.ownerDocument,
            'li',
            'article-default__toc-item article-default__toc-item--h3',
          )
          item.append(createLink(heading, 'h3'))
          sublist.append(item)
        }
        groupItem.append(sublist)
      }

      list.append(groupItem)
    }

    const setActive = (heading) => {
      for (const link of linksByHeading.values()) {
        link.classList?.remove?.('is-active')
        link.removeAttribute?.('aria-current')
      }
      for (const group of list.querySelectorAll?.('.article-default__toc-group') || []) {
        group.classList?.remove?.('is-active')
      }

      const link = linksByHeading.get(heading)
      if (!link) return
      link.classList?.add?.('is-active')
      link.setAttribute?.('aria-current', 'location')
      link.closest?.('.article-default__toc-group')?.classList?.add?.('is-active')
    }

    const updateActive = () => setActive(activeArticleHeading(headings))
    toc.replaceChildren(list)
    updateActive()
    this._scope.listen(globalThis, 'scroll', updateActive, { passive: true })
  }

  _initializeSidebar() {
    const toggle = this.querySelector?.('[data-article-sidebar-toggle]')
    if (!toggle) return

    this._scope.listen(toggle, 'click', () => {
      const collapsed = this.classList?.toggle?.('article-default--sidebar-collapsed') || false
      toggle.setAttribute?.('aria-expanded', String(!collapsed))
    })
  }
}

export class ArticleDefaultRecommendations extends HTMLElementBase {
  connectedCallback() {
    this.disconnectedCallback()
    this._scope = createListenerScope()
    this._gate = createRequestGate()
    this.load()
  }

  disconnectedCallback() {
    this._scope?.abort()
    this._gate?.abort()
    this._scope = undefined
    this._gate = undefined
  }

  async load() {
    const list = this.querySelector?.('[data-article-recommendation-list]')
    const status = this.querySelector?.('[data-article-recommendation-status]')
    if (!list || !status || !this._gate) return
    const blogs = parseJSON(this.dataset.blogHandles, [])
    const currentTags = parseJSON(this.dataset.currentTags, [])
    const request = this._gate.begin()
    this.hidden = false
    status.hidden = false
    status.textContent = this.dataset.loadingMessage || 'Loading recommendations'

    try {
      const data = await loadRecommendationArticles(blogs, { signal: request.signal })
      if (!request.isCurrent()) return
      const articles = rankRecommendations(data.articles, {
        currentUrl: this.dataset.currentArticleUrl,
        currentTags,
        currentBlogTitle: this.dataset.currentBlogTitle,
      })
      this._renderArticles(list, articles)
      const message = articles.length
        ? ''
        : data.failed
          ? this.dataset.errorMessage
          : this.dataset.emptyMessage
      status.textContent = message || ''
      status.hidden = !message
    } catch (error) {
      if (error?.name === 'AbortError') return
      if (!request.isCurrent()) return
      list.replaceChildren()
      status.textContent = this.dataset.errorMessage || 'Recommendations unavailable'
      status.hidden = false
      this.hidden = false
    }
  }

  _renderArticles(list, articles) {
    const documentRef = this.ownerDocument
    if (!documentRef) return
    const cards = articles.map((article) => {
      const item = element(documentRef, 'li', 'article-default__recommendation')
      const link = element(documentRef, 'a', 'article-default__recommendation-link')
      link.href = article.url
      link.setAttribute('aria-label', article.title || article.url)
      if (article.image) {
        const image = element(documentRef, 'img', 'article-default__recommendation-image')
        image.src = article.image
        image.alt = article.title || ''
        image.loading = 'lazy'
        image.width = 608
        image.height = 342
        link.append(image)
      }
      link.append(
        element(documentRef, 'h3', 'article-default__recommendation-title', article.title || ''),
      )
      if (article.excerpt) {
        link.append(
          element(documentRef, 'p', 'article-default__recommendation-excerpt', article.excerpt),
        )
      }
      item.append(link)
      if (article.blogTitle || article.dateText) {
        const meta = element(documentRef, 'div', 'article-default__recommendation-meta')
        if (article.blogTitle) {
          const blog = element(
            documentRef,
            article.blogUrl ? 'a' : 'span',
            `article-default__recommendation-blog${
              article.dateText ? ' article-default__recommendation-blog--separated' : ''
            }`,
            article.blogTitle,
          )
          if (article.blogUrl) blog.href = article.blogUrl
          meta.append(blog)
        }
        if (article.dateText) {
          meta.append(
            element(documentRef, 'span', 'article-default__recommendation-date', article.dateText),
          )
        }
        item.append(meta)
      }
      return item
    })
    list.replaceChildren(...cards)
  }
}

if (globalThis.customElements && !globalThis.customElements.get('article-default-main')) {
  globalThis.customElements.define('article-default-main', ArticleDefaultMain)
}

if (
  globalThis.customElements &&
  !globalThis.customElements.get('article-default-recommendations')
) {
  globalThis.customElements.define('article-default-recommendations', ArticleDefaultRecommendations)
}
